import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import React from 'react';
import ExecutionConstants, { ActionState } from './ExecutionConstants';
import { ExecutionStatus } from './ExecutionConstants';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex'
        },
        teardownStageIcon: {
            paddingLeft: 4,
            position: 'relative',
            '&::after': {
                content: '""',
                width: 8,
                position: 'absolute',
                left: -2,
                top: 12,
                borderBottom: `2px solid ${theme.palette.divider}`,
            }
        },
        icon: {
            fontSize: '1.65rem'
        }
    })
);

interface Props {
    executionPlan: string;
    executionStatus: ExecutionStatus,
    deploymentList: any[];
    actionName: string;
}

function getDeployStageIcon(action, executionStatus, classes) {
    let state = ActionState.Pending;
    if (executionStatus === ExecutionStatus.AwaitingApprovals ||
        executionStatus === ExecutionStatus.Scheduled ||
        executionStatus === ExecutionStatus.Ready) {
        state = ActionState.NotStarted;
    } else if (!action) {
        state = ActionState.Pending;
    } else if ((action.Errors && action.Errors.length) || action.DeploymentState.includes('FAILED')) {
        state = ActionState.Failed;
    } else if (action.DeploymentState === 'COMPLETE') {
        state = ActionState.Passed;
    } else {
        state = ActionState.InProgress;
    }

    if (executionStatus === ExecutionStatus.Failed && state === ActionState.Pending) {
        state = ActionState.NotStarted;
    } else if (executionStatus === ExecutionStatus.Failed && state === ActionState.InProgress) {
        state = ActionState.Unknown;
    } else if (executionStatus === ExecutionStatus.Passed && state === ActionState.Pending) {
        state = ActionState.Unknown;
    }

    const Icon = ExecutionConstants.ActionStateIcons[state];

    return (
        <Tooltip title={`Deployment is ${ExecutionConstants.ActionStateLabels[state]}`}>
            <Icon style={{ color: ExecutionConstants.ActionStateColors[state] }} className={classes.icon} />
        </Tooltip>
    );
}

function getTeardownStageIcon(action, executionStatus, classes) {
    let state = ActionState.Pending;
    if (executionStatus === ExecutionStatus.AwaitingApprovals ||
        executionStatus === ExecutionStatus.Scheduled ||
        executionStatus === ExecutionStatus.Ready) {
        state = ActionState.NotStarted;
    } else if (!action) {
        state = ActionState.Pending;
    } else if ((action.Errors && action.Errors.length) || action.TeardownState.includes('FAILED')) {
        state = ActionState.Failed;
    } else if (action.TeardownState === 'NOT_ATTEMPTED') {
        state = ActionState.Pending;
    } else if (action.TeardownState === 'TORN_DOWN') {
        state = ActionState.Passed;
    } else {
        state = ActionState.InProgress;
    }

    if (executionStatus === ExecutionStatus.Failed && state === ActionState.Pending) {
        state = ActionState.NotStarted;
    } else if (executionStatus === ExecutionStatus.Failed && state === ActionState.InProgress) {
        state = ActionState.Unknown;
    } else if (executionStatus === ExecutionStatus.Passed && state === ActionState.Pending) {
        state = ActionState.Unknown;
    }

    const Icon = ExecutionConstants.ActionStateIcons[state];

    return (
        <Tooltip title={`Teardown is ${ExecutionConstants.ActionStateLabels[state]}`}>
            <Icon style={{ color: ExecutionConstants.ActionStateColors[state] }} className={classes.icon} />
        </Tooltip>
    );
}

function ExecutionActionStageIcons(props: Props) {
    const { executionPlan, executionStatus, deploymentList, actionName } = props;
    const classes = useStyles();

    const hasDeployStage = executionPlan.includes('DEPLOY');
    const hasTeardownStage = executionPlan === 'DEPLOY_WITH_TEARDOWN' || executionPlan === 'DEPLOY_TEARDOWN' || executionPlan === 'TEARDOWN_ALL';

    const action = deploymentList.find(item => item.ActionName === actionName);

    return (
        <div className={classes.root}>
            {hasDeployStage && <div>
                {getDeployStageIcon(action, executionStatus, classes)}
            </div>}
            {hasTeardownStage && <div className={hasDeployStage ? classes.teardownStageIcon : null}>
                {getTeardownStageIcon(action, executionStatus, classes)}
            </div>}
        </div>
    );
};

export default ExecutionActionStageIcons;
